import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import apiService from '../../services/api';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { Link } from 'react-router-dom';

const { FiLogOut, FiUser, FiInfo, FiMessageSquare } = FiIcons;

const Header = () => {
  const { user, logout } = useAuth();
  const [announcement, setAnnouncement] = useState(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const textRef = useRef(null);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const fetchAnnouncement = async () => {
      try {
        const response = await apiService.getAnnouncements();
        if (response.data) {
          const active = response.data.find(a => a.is_active === 1);
          setAnnouncement(active ? active.text : null);
        }
      } catch (error) {
        console.error("Failed to fetch announcements", error);
      }
    };

    fetchAnnouncement();
    const interval = setInterval(fetchAnnouncement, 60000);
    return () => clearInterval(interval);
  }, []);

  useLayoutEffect(() => {
    const checkOverflow = () => {
      if (textRef.current && wrapperRef.current) {
        const textWidth = textRef.current.scrollWidth;
        const wrapperWidth = wrapperRef.current.clientWidth;
        const availableWidth = wrapperWidth - 112; 
        setIsOverflowing(textWidth > availableWidth);
      }
    };
    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [announcement]);

  const handleLogout = () => {
    logout();
  };
  
  const animationDuration = announcement ? Math.max(20, announcement.length * 0.3) : 20;

  return (
    <header className="bg-white shadow-sm border-b border-gray-200 relative z-10">
      <style>{`
        @keyframes marquee {
          0% { transform: translateX(100%); }
          100% { transform: translateX(-100%); }
        }
        .animate-marquee {
          display: inline-block;
          white-space: nowrap;
          animation-name: marquee;
          animation-timing-function: linear;
          animation-iteration-count: infinite;
          will-change: transform;
        }
        .animate-marquee:hover {
          animation-play-state: paused;
        }
        .mask-gradient {
          mask-image: linear-gradient(to right, transparent, black 20px, black calc(100% - 20px), transparent);
          -webkit-mask-image: linear-gradient(to right, transparent, black 20px, black calc(100% - 20px), transparent);
        }
      `}</style>
      <div className="w-full px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center gap-4 flex-shrink-0">
            <img 
              src="https://www.remotely.de/images/logos/17767_efs-techhub-gmbh_logo.png" 
              alt="EFS TechHub Logo" 
              className="h-12 w-auto object-contain"
            />
            <h1 className="text-xl font-semibold text-brand-dark hidden sm:block">Team Collaboration Hub</h1>
          </div>
          
          {announcement && (
            <div className="flex-1 flex justify-center px-8 overflow-hidden" ref={wrapperRef}>
              <div 
                className={`flex items-center justify-center ${isOverflowing ? 'w-full' : ''}`}
              >
                <div 
                  className={`
                    relative
                    ${isOverflowing ? 'w-full overflow-hidden rounded-md py-1 mask-gradient' : 'px-6 py-1.5 rounded-full'}
                  `}
                  style={{ backgroundColor: '#87ff8f' }}
                >
                  <div 
                    ref={textRef}
                    className={`
                      text-brand-secondary font-semibold text-center
                      ${isOverflowing ? 'animate-marquee min-w-full px-4' : 'truncate'}
                    `}
                    style={isOverflowing ? { animationDuration: `${animationDuration}s` } : {}}
                  >
                    {announcement}
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <div className="flex items-center space-x-4 flex-shrink-0" data-tour="header-profile">
            <span className="text-sm text-gray-700 hidden md:block">
              Willkommen, {user?.firstname} {user?.lastname}
            </span>
            
            <div className="flex items-center space-x-2">
              <Link
                to="/release-notes"
                className="p-2 text-gray-600 hover:text-brand-secondary hover:bg-brand-primary/10 rounded-md transition-colors"
                title="Release Notes"
              >
                <SafeIcon icon={FiInfo} className="w-5 h-5" />
              </Link>
              
              <Link
                to="/feedback"
                className="p-2 text-gray-600 hover:text-brand-secondary hover:bg-brand-primary/10 rounded-md transition-colors"
                title="Feature & Bug Request"
              >
                <SafeIcon icon={FiMessageSquare} className="w-5 h-5" />
              </Link>

              <Link
                to="/profile"
                className="p-2 text-gray-600 hover:text-brand-secondary hover:bg-brand-primary/10 rounded-md transition-colors"
                title="Mein Profil"
              >
                <SafeIcon icon={FiUser} className="w-5 h-5" />
              </Link>
              
              <button
                onClick={handleLogout}
                className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                title="Abmelden"
              >
                <SafeIcon icon={FiLogOut} className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;